import React, { useState, useEffect, useCallback, ForwardedRef, forwardRef, useImperativeHandle, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client';
import { ICategory, IOrg } from '../../../../types/org';
import { GET_INFOGO_ASSOCIATED_ORGS } from '../../../../graphql/infogo-gql';
import { IInfoGoOrg, IPresentationInput } from '../../../../types/infogo';
import { PresentationObject } from '../../../../types/constants';
import SpinnerOverlay from '../../../common/spinner/spinner';
import { DndContext, closestCenter, DragOverlay, useSensors, useSensor, MouseSensor, KeyboardSensor } from '@dnd-kit/core';
import { arrayMove } from '../../../../utils/commons';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import '../infogo-config.scss';
import AssociatedOrgItem from './associated-org-item';
import useAuth from '../../../../hook/useAuth';

export type AssociatedOrgsHandler = {
    getAssociatedOrgsConfig: () => IPresentationInput[];
    reset: () => void;
    hasChange: () => boolean;
    hasNewSuppress: () => boolean;
}

type AssocitedOrgsProps = {
    org?: IOrg;
    onChange: () => void;
}

const AssociatedOrgs = forwardRef<AssociatedOrgsHandler, AssocitedOrgsProps>((props: AssocitedOrgsProps, ref:ForwardedRef<any>) => {
    const { org, onChange } = props
    const [ orgs, setOrgs ] = useState<IInfoGoOrg[]>([])

    const [ orgCategories, setOrgCategories ] = useState<ICategory[]>([])
    const [ associatedOrgsMap, setAssociatedOrgsMap ] = useState<Map<number, IInfoGoOrg[]>>(new Map<number, IInfoGoOrg[]>())

    const [ suppressedIds, setSuppressedIds ] = useState<string[]>([])

    const [ prevSuppress, setPrevSuppress ] = useState<string[]>([])
    const [ prevSequence, setPrevSequence ] = useState<string[]>([])

    const [activeId, setActiveId] = useState<string | null>(null)

    const { isFrenchTranslator } = useAuth()

    const [getAssociatedOrgs, { loading, data }] = useLazyQuery(GET_INFOGO_ASSOCIATED_ORGS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.infoGoAssociatedOrgs) {
                const suppressVls = data.infoGoAssociatedOrgs.filter((e: IInfoGoOrg) => e.suppress).map((e: IInfoGoOrg) => e.relationshipId)
                setPrevSuppress(suppressVls)
                setSuppressedIds(suppressVls)
                const sequenceVls = data.infoGoAssociatedOrgs.map((e: IInfoGoOrg) => e.relationshipId)
                setPrevSequence(sequenceVls)
            }
            setOrgs(data?.infoGoAssociatedOrgs ? [...data.infoGoAssociatedOrgs] : [])
        }
    })

    useEffect(() => {
        let categories: ICategory[] = []
        let category2OrgMap = new Map<number, IInfoGoOrg[]>()
        if (orgs) {
            orgs.forEach((org: IInfoGoOrg) => {
                if (!categories.some((cat: ICategory) => cat.id === org.category.id)) {
                    categories.push(org.category)
                }

                if (!category2OrgMap.get(org.category.id)) {
                    category2OrgMap.set(org.category.id, [])
                }
                category2OrgMap.get(org.category.id)?.push(org)
            })
        }
        categories.sort((a: ICategory, b: ICategory) => a.displayOrder - b.displayOrder)
        setOrgCategories(categories)
        setAssociatedOrgsMap(category2OrgMap)
    }, [orgs])

    useImperativeHandle(ref, () => ({
        getAssociatedOrgsConfig() {
            let input: IPresentationInput[] = orgs.map((org: IInfoGoOrg, index: number) => {
                return {
                    typeName: PresentationObject.GENERAL_RELATIONSHIP,
                    objectId: org.relationshipId,
                    suppress: suppressedIds.includes(org.relationshipId) ? true: false,
                    sequence: index + 1
                }
            })
            return input
        },
        reset() {
            onReset()
        },
        hasChange() {
            return reOrdered() || hasSuppressChange()
        },
        hasNewSuppress() {
            return hasNewSuppressChange()
        }
    }),[data, suppressedIds, orgs])

    useEffect(() => {
        if (org) {
            getAssociatedOrgs({ variables: { orgId: org.id } })
        }
    }, [org])

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 8
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const onReset = () => {
        setOrgs([...data?.infoGoAssociatedOrgs])
        setSuppressedIds(prevSuppress)
        setTimeout(()=>onChange())
    }

    const onSuppressCheck = (event: any, id: string) => {
        if (event.target.checked) {
            setSuppressedIds([...suppressedIds, id])
        } else {
            setSuppressedIds(suppressedIds.filter(e => e != id))
        }
        setTimeout(()=>onChange())
    }

    const reOrdered = (): boolean => {
        const newSequence = orgs.map((e: IInfoGoOrg) => e.relationshipId)
        if (prevSequence.length > 0) {
            return prevSequence.some((value, index) => value !== newSequence[index])
        }
        return false;
    }

    const hasSuppressChange = (): boolean => {
        if (prevSuppress.length !== suppressedIds.length) {
            return true
        }
        if (prevSuppress.length > 0) {
            return prevSuppress.some((value) => !suppressedIds.includes(value))
        }
        return false
    }

    const hasNewSuppressChange = (): boolean => {
        if (suppressedIds.length > prevSuppress.length) {
            return true
        }
        if (suppressedIds.length > 0) {
            return suppressedIds.some((value) => !prevSuppress.includes(value))
        }
        return false
    }

    const handleDragStart = (event: any) => {
        const { active } = event
        if (active && active.id) {
            setActiveId(active.id)
        }
    }

    const handleDragEnd = (event: any) => {
        const { active, over } = event
        if (over && active.id !== over.id) {
            setOrgs((orgList: IInfoGoOrg[]) => {
                const oldIndex = orgList.findIndex((item) => item.relationshipId === active.id);
                const newIndex = orgList.findIndex((item) => item.relationshipId === over.id);
                return arrayMove(orgList, oldIndex, newIndex);
            })
        }
        setActiveId(null)
        setTimeout(()=>onChange())
    }

    const getOverlayOrg = () => {
        return orgs.find((org: IInfoGoOrg) => org.relationshipId === activeId) as IInfoGoOrg
    }

    return (
        <div>
            {loading && <SpinnerOverlay />}

            {
                orgs.length === 0
                ?
                <div style={{ textAlign: 'center' }}>No associated organizations found.</div>
                :
                <>
                    <div className="infogo-grid-row grid-header">
                        <div className="infogo-assorg-col1">
                        </div>
                        <div className="infogo-assorg-col2">
                            Organization
                        </div>
                        <div className="infogo-assorg-col3">
                            Suppressed?
                        </div>
                    </div>
                    <div>
                        {!isFrenchTranslator && <DndContext sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragCancel={() => setActiveId(null)}>
                            <>
                            {
                                orgCategories?.map((cat: ICategory, index: number) =>
                                <>
                                    <div className="infogo-category">{cat.categoryName}</div>
                                    <SortableContext
                                        key = {index}
                                        strategy={verticalListSortingStrategy}
                                        items={associatedOrgsMap.get(cat.id)!.map((org: IInfoGoOrg) => org.relationshipId)}>
                                        <div>
                                            {associatedOrgsMap.get(cat.id)?.map((org: IInfoGoOrg) => (
                                                <AssociatedOrgItem
                                                    key={org.relationshipId}
                                                    id={org.relationshipId}
                                                    suppressed={suppressedIds.includes(org.relationshipId)}
                                                    onSuppressCheck={event => onSuppressCheck(event, org.relationshipId)}
                                                    org={org}
                                                    visible={org.relationshipId !== activeId}/>
                                            ))}
                                        </div>
                                    </SortableContext>
                                </>
                            )}
                             <DragOverlay>
                                {activeId &&
                                    <AssociatedOrgItem
                                        id={activeId}
                                        org={getOverlayOrg()}
                                        suppressed={suppressedIds.includes(activeId)}
                                        onSuppressCheck={()=>{}}
                                        visible
                                        isDragging />
                                }
                            </DragOverlay>
                            </>
                        </DndContext>}
                        {isFrenchTranslator && <div>
                            {
                                orgCategories?.map((cat: ICategory, index: number) =>
                                <>
                                    <div className="infogo-category">{cat.categoryName}</div>
                                    <SortableContext
                                        key = {index}
                                        strategy={verticalListSortingStrategy}
                                        items={associatedOrgsMap.get(cat.id)!.map((org: IInfoGoOrg) => org.relationshipId)}>
                                        <div>
                                            {associatedOrgsMap.get(cat.id)?.map((org: IInfoGoOrg) => (
                                                <AssociatedOrgItem
                                                    key={org.relationshipId}
                                                    id={org.relationshipId}
                                                    suppressed={suppressedIds.includes(org.relationshipId)}
                                                    onSuppressCheck={event => onSuppressCheck(event, org.relationshipId)}
                                                    org={org}
                                                    visible={org.relationshipId !== activeId}/>
                                            ))}
                                        </div>
                                    </SortableContext>
                                </>
                            )}
                        </div>

                        }
                     </div>
                </>
            }
        </div>
    )
})

export default AssociatedOrgs
